import { AbsurdIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Security } from '@ipfabric/utils';
import { setProp } from '@ipfabric/utils/fp/prop';
import {
  isResolvedReferencedTest,
  isSecurityReferenceResolvedError,
} from 'components/SecuritySchema/model/ResolvedReferencedTest';
import type {
  ComparisonTestNode,
  ErrorNode,
  ErrorType,
  EvalTestNode,
  GroupTestNode,
  TestMetaInfo,
  TestNode,
  TrivialTestNode,
} from 'components/SecuritySchema/model/SecuritySchemaNode';
import { dereferenceValue } from 'components/SecuritySchema/model/helpers/dereferenceValue';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';

export type SecurityNodeResolutionScope = {
  deviceSecurity: Security.DeviceSecurity;
  type: Security.SecurityType | 'referencedTest';
  /** @internal */
  visitedReferenceKeys?: string[];
};

const getTestMetaInfo = ({
  category,
  description,
  name,
}: TestMetaInfo): TestMetaInfo => ({
  category,
  description,
  name,
});

const nodeFromAndTest =
  (resolutionScope: SecurityNodeResolutionScope) =>
  (test: Security.AndGroupTest): GroupTestNode => ({
    children: test.items.map(securitySchemaNodeFromTest(resolutionScope)),
    group: 'and',
    info: getTestMetaInfo(test),
    type: 'group',
  });

const nodeFromOrTest =
  (resolutionScope: SecurityNodeResolutionScope) =>
  (test: Security.OrGroupTest): GroupTestNode => ({
    children: test.items.map(securitySchemaNodeFromTest(resolutionScope)),
    group: 'or',
    info: getTestMetaInfo(test),
    type: 'group',
  });

const nodeFromNotTest =
  (resolutionScope: SecurityNodeResolutionScope) =>
  (test: Security.NotGroupTest): GroupTestNode => ({
    children: [securitySchemaNodeFromTest(resolutionScope)(test.item)],
    group: 'not',
    info: getTestMetaInfo(test),
    type: 'group',
  });

const nodeFromTrivialTest = (test: Security.TrivialTest): TrivialTestNode => ({
  children: array.empty,
  info: getTestMetaInfo(test),
  operator: test.operator,
  type: 'trivial',
  original: test.original,
});

const nodeFromComparisonTest =
  (resolutionScope: SecurityNodeResolutionScope) =>
  (test: Security.ComparisonTest): ComparisonTestNode => ({
    children: array.empty,
    info: getTestMetaInfo(test),
    left: test.left,
    operator: test.operator,
    right: Security.isReferencedValue(test.right)
      ? dereferenceValue(resolutionScope)(test.right)
      : test.right,
    type: 'comparison',
  });

const nodeFromSimpleTest =
  (resolutionScope: SecurityNodeResolutionScope) =>
  (test: Security.SimpleTest): TestNode =>
    Security.isTrivialTest(test)
      ? nodeFromTrivialTest(test)
      : nodeFromComparisonTest(resolutionScope)(test);

const nodeFromEvalTest = (test: Security.EvalTest): EvalTestNode => ({
  children: [],
  info: getTestMetaInfo(test),
  chainName: test.chainName,
  chainType: test.chainType,
  checkType: test.checkType,
  type: 'eval',
});

const getReferenceMetaInfo = (test: Security.ReferencedTest) => ({
  ...getTestMetaInfo(test),
  reference: test.reference,
});

const createReferenceErrorNode = (
  test: Security.ReferencedTest,
  errorType: ErrorType,
): ErrorNode => ({
  children: array.empty,
  errorType,
  referenceInfo: getReferenceMetaInfo(test),
  type: 'error',
});

const nodeFromReferencedTest =
  (resolutionScope: SecurityNodeResolutionScope) =>
  (test: Security.ReferencedTest): TestNode | ErrorNode => {
    if (!isResolvedReferencedTest(test)) {
      stumble(
        'Referenced tests should have info fetched by resolveReferencedTests after loading from server',
      );
      return createReferenceErrorNode(test, 'unresolvedReference');
    }
    if (isSecurityReferenceResolvedError(test.resolvedReference)) {
      return createReferenceErrorNode(
        test,
        (
          {
            circular: 'circularReference',
            notFound: 'notFoundReference',
          } as const
        )[test.resolvedReference],
      );
    }

    if (test.resolvedReferenceKey === null) {
      stumble(
        'Properly referenced tests should contain resolved reference keys',
      );
      return createReferenceErrorNode(test, 'unresolvedReference');
    }
    if (
      resolutionScope.visitedReferenceKeys?.includes(test.resolvedReferenceKey)
    ) {
      return createReferenceErrorNode(test, 'circularRecursionReference');
    }

    return pipe(
      test.resolvedReference,
      securitySchemaNodeFromTest({
        ...resolutionScope,
        visitedReferenceKeys: [
          ...(resolutionScope.visitedReferenceKeys ?? array.empty),
          test.resolvedReferenceKey,
        ],
      }),
      setProp('referenceInfo', getReferenceMetaInfo(test)),
    );
  };

export const securitySchemaNodeFromTest =
  (resolutionScope: SecurityNodeResolutionScope) =>
  (test: Security.Test): TestNode | ErrorNode => {
    if (Security.isAndGroupTest(test)) {
      return nodeFromAndTest(resolutionScope)(test);
    }
    if (Security.isNotGroupTest(test)) {
      return nodeFromNotTest(resolutionScope)(test);
    }
    if (Security.isOrGroupTest(test)) {
      return nodeFromOrTest(resolutionScope)(test);
    }
    if (Security.isReferencedTest(test)) {
      return nodeFromReferencedTest(resolutionScope)(test);
    }
    if (Security.isSimpleTest(test)) {
      return nodeFromSimpleTest(resolutionScope)(test);
    }
    if (Security.isEvalTest(test)) {
      return nodeFromEvalTest(test);
    }
    throw new AbsurdIpfError(test, 'unknown security test node type');
  };
